import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import QRCode from "qrcode";
import OakLogo from "@/components/OakLogo";
import LoadingScreen from "@/components/LoadingScreen";
import InfraBadges, { INFRA_DISCLAIMER } from "@/components/InfraBadges";
import { api } from "@/lib/api";
import { CheckCircle2, Award, ShieldAlert, ExternalLink } from "lucide-react";

/**
 * B4 — Public, auth-free verifiable certificate page.
 * Anyone with the cert_id (printed on the cert PNG/PDF) can confirm
 * authenticity at /cert/{cert_id}. Backend strips email + internal
 * user_id; only display name + goal + mastery level are exposed.
 */
export default function CertificatePublic() {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);

  useEffect(() => {
    api
      .get(`/certificate/public/${certId}`)
      .then(({ data }) => setCert(data))
      .catch((e) => setError(e?.response?.data?.detail || "Certificate not found"));
  }, [certId]);

  // Same Open Badges 2.0 verification QR as the private page.
  useEffect(() => {
    if (!certId) return;
    const verifyUrl = `${window.location.origin}/cert/${certId}`;
    QRCode.toDataURL(verifyUrl, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 220,
      color: { dark: "#FFFFFF", light: "#00000000" },
    })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [certId]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center px-6" data-testid="cert-public-not-found">
        <div className="glass p-8 max-w-md w-full text-center space-y-3">
          <ShieldAlert size={28} className="text-rose-300 mx-auto" />
          <h1 className="font-display text-2xl tracking-tight text-white">Certificate not found</h1>
          <p className="text-white/55 text-sm">
            The ID <span className="font-mono-oak text-white/75">{certId}</span> doesn't match any
            issued Oak AI Campus certificate.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#8aa8ff] hover:text-white transition"
          >
            Visit Oak AI Campus <ExternalLink size={12} />
          </Link>
        </div>
      </div>
    );
  }

  if (!cert) return <LoadingScreen label="Verifying certificate…" />;

  return (
    <div className="min-h-screen px-6 md:px-10 py-10" data-testid="cert-public-page">
      <header className="max-w-4xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <OakLogo size={28} glow />
          <div className="leading-tight">
            <div className="font-display font-semibold text-sm">Oak AI Campus</div>
            <div className="label-eyebrow !text-[9px]">Verify Certificate</div>
          </div>
        </Link>
        <span
          className="inline-flex items-center gap-1.5 rounded-full border border-[#00FF9D]/40 bg-[#00FF9D]/[0.08] px-3 py-1 text-[11px] text-[#00FF9D]"
          data-testid="cert-public-verified-badge"
        >
          <CheckCircle2 size={12} /> Verified
        </span>
      </header>

      <main className="max-w-4xl mx-auto mt-10">
        <div
          className="relative rounded-3xl overflow-hidden border border-white/15 p-8 md:p-14"
          style={{
            background:
              "radial-gradient(900px circle at 15% 0%, rgba(255,123,0,0.25), transparent 55%), radial-gradient(700px circle at 100% 100%, rgba(93,63,211,0.35), transparent 55%), linear-gradient(180deg, #0B0E14 0%, #131720 100%)",
          }}
          data-testid="cert-public-card"
        >
          <div className="flex items-center justify-between gap-3 mb-10">
            <div className="flex items-center gap-3">
              <OakLogo size={32} glow />
              <div>
                <div className="font-display font-semibold text-white">Oak AI Campus</div>
                <div className="label-eyebrow !text-[10px]">Universal Certificate of Mastery</div>
              </div>
            </div>
            <div className="text-[11px] text-white/45 font-mono-oak" data-testid="cert-public-id">
              {cert.cert_id}
            </div>
          </div>

          <div className="text-white/55 text-xs tracking-[0.3em] uppercase">Awarded To</div>
          <div className="font-display text-3xl md:text-5xl tracking-tight mt-2 text-white" data-testid="cert-public-name">
            {cert.name}
          </div>

          <div className="text-white/65 mt-5 max-w-2xl text-[15px] leading-relaxed">
            For successfully completing the Oak AI Campus mastery track on
          </div>
          <div
            className="font-display text-xl md:text-2xl mt-2 bg-gradient-to-r from-[#00FF9D] via-[#0044FF] to-[#FF7B00] bg-clip-text text-transparent uppercase tracking-tight"
            style={{ WebkitBackgroundClip: "text" }}
            data-testid="cert-public-goal"
          >
            “{(cert.goal || "").toUpperCase()}”
          </div>

          {cert.mastery_level && (
            <div
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#ffb067]/40 bg-[#ffb067]/[0.08] px-4 py-1.5"
              data-testid="cert-public-mastery"
            >
              <Award size={14} className="text-[#ffb067]" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#ffb067]/85">Mastery Level</span>
              <span className="font-display font-semibold text-[#ffb067] text-sm">{cert.mastery_level}</span>
            </div>
          )}

          <div className="mt-10 flex items-end justify-between gap-5 flex-wrap">
            <div>
              <div className="text-[11px] text-white/50">Issued</div>
              <div className="font-mono-oak text-sm text-white/80">
                {cert.issued_at
                  ? new Date(cert.issued_at).toLocaleDateString(undefined, {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "—"}
              </div>
            </div>
            {qrDataUrl && (
              <div className="flex flex-col items-center gap-1" data-testid="cert-public-qr">
                <img
                  src={qrDataUrl}
                  alt={`Verify ${certId}`}
                  className="w-[80px] h-[80px] rounded-md border border-white/15 bg-black/40 p-1"
                />
                <div className="text-[8px] text-white/40 tracking-[0.25em] uppercase font-mono-oak">
                  Open Badges 2.0
                </div>
              </div>
            )}
            <div className="text-right">
              <div className="font-display italic text-xl text-white/85" data-testid="cert-public-issuer">
                Issued by Oak AI Campus
              </div>
              <div className="text-[11px] text-white/45">
                Digiluxcon Technology Services (OPC) Pvt. Ltd.
              </div>
            </div>
          </div>

          {/* Validation Bar — same brief as private cert. */}
          <div
            className="mt-8 pt-5 border-t border-white/8 flex flex-col items-center gap-2.5"
            data-testid="cert-public-validation-bar"
          >
            <InfraBadges compact />
            <p
              className="text-white/35 leading-snug text-center max-w-3xl font-mono-oak"
              style={{ fontSize: "7pt" }}
            >
              {INFRA_DISCLAIMER}
            </p>
          </div>
        </div>

        <p className="text-center text-white/45 text-xs mt-8">
          Verified by Oak AI Campus. To learn how to earn yours, visit{" "}
          <Link to="/" className="text-[#8aa8ff] hover:text-white">
            oakaiapp.com
          </Link>
          .
        </p>
      </main>
    </div>
  );
}
